import React, { useState } from "react";
import {
  Modal, SpaceBetween, FormField, DatePicker, Input, Select, Multiselect, Button, Box,
  Tabs, Alert,
} from "@cloudscape-design/components";
import { ShiftTypeConfig, BulkEntry } from "../types";
import { apiClient } from "../api/client";
import { CsvUpload } from "./CsvUpload";

interface Props {
  visible: boolean;
  onDismiss: () => void;
  onSave: () => void;
  siteId: string;
  availableTrades: string[];
  shiftTypes: ShiftTypeConfig[];
}

interface BulkRow {
  id: string;
  title: string;
  date: string;
  shift: string;
  trades: string[];
  description: string;
}

export const BulkAddModal: React.FC<Props> = ({ visible, onDismiss, onSave, siteId, availableTrades, shiftTypes }) => {
  const [activeTab, setActiveTab] = useState("bulk");
  const [rows, setRows] = useState<BulkRow[]>([
    { id: "1", title: "", date: "", shift: shiftTypes[0]?.id || "day", trades: [], description: "" },
    { id: "2", title: "", date: "", shift: shiftTypes[0]?.id || "day", trades: [], description: "" },
    { id: "3", title: "", date: "", shift: shiftTypes[0]?.id || "day", trades: [], description: "" },
  ]);
  const [csvEntries, setCsvEntries] = useState<BulkEntry[]>([]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<string>("");

  const shiftOptions = shiftTypes.map(st => ({ value: st.id, label: st.name }));
  const tradeOptions = availableTrades.map(t => ({ value: t, label: t }));

  const addRow = () => {
    setRows(prev => [...prev, { id: String(Date.now()), title: "", date: "", shift: shiftTypes[0]?.id || "day", trades: [], description: "" }]);
  };

  const removeRow = (id: string) => {
    setRows(prev => prev.filter(r => r.id !== id));
  };

  const updateRow = (id: string, field: keyof BulkRow, value: any) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  const handleSave = async () => {
    setSaving(true);
    setResult("");
    try {
      let entries: BulkEntry[];
      if (activeTab === "csv") {
        entries = csvEntries;
      } else {
        entries = rows
          .filter(r => r.title.trim() && r.date)
          .map(r => ({
            title: r.title.trim(),
            date: r.date,
            shift: r.shift,
            trades: r.trades,
            description: r.description.trim() || undefined,
          }));
      }

      if (entries.length === 0) {
        setResult("No valid entries to add");
        setSaving(false);
        return;
      }

      const data = await apiClient.post("/events/bulk", { siteId, entries });
      setResult(`Successfully created ${data.created} events`);
      onSave();
      setTimeout(() => onDismiss(), 1500);
    } catch (err: any) {
      setResult(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Add Maintenance Entries"
      size="max"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onDismiss}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={saving}>Add Entries</Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {result && <Alert type={result.startsWith("Error") ? "error" : "success"}>{result}</Alert>}

        <Tabs
          activeTabId={activeTab}
          onChange={({ detail }) => setActiveTab(detail.activeTabId)}
          tabs={[
            {
              id: "bulk",
              label: "Bulk Entry",
              content: (
                <SpaceBetween size="m">
                  <Box variant="small" color="text-status-inactive">
                    Add multiple entries. Each row has its own title, date, shift, trades, and optional description.
                  </Box>
                  {rows.map(row => (
                    <div key={row.id} style={{ display: "grid", gridTemplateColumns: "1fr 150px 130px 180px 1fr 40px", gap: 8, alignItems: "center" }}>
                      <Input value={row.title} onChange={({ detail }) => updateRow(row.id, "title", detail.value)} placeholder="Task title" />
                      <DatePicker value={row.date} onChange={({ detail }) => updateRow(row.id, "date", detail.value)} placeholder="YYYY/MM/DD" />
                      <Select
                        selectedOption={shiftOptions.find(o => o.value === row.shift) || shiftOptions[0]}
                        options={shiftOptions}
                        onChange={({ detail }) => updateRow(row.id, "shift", detail.selectedOption.value || "day")}
                      />
                      <Multiselect
                        selectedOptions={row.trades.map(t => ({ value: t, label: t }))}
                        options={tradeOptions}
                        onChange={({ detail }) => updateRow(row.id, "trades", detail.selectedOptions.map((o: any) => o.value))}
                        placeholder="Trades"
                        filteringType="auto"
                      />
                      <Input value={row.description} onChange={({ detail }) => updateRow(row.id, "description", detail.value)} placeholder="Description (optional)" />
                      <Button variant="icon" iconName="remove" onClick={() => removeRow(row.id)} />
                    </div>
                  ))}
                  <Button onClick={addRow} iconName="add-plus">Add Row</Button>
                </SpaceBetween>
              ),
            },
            {
              id: "csv",
              label: "CSV Upload",
              content: <CsvUpload onParsed={setCsvEntries} />,
            },
          ]}
        />
      </SpaceBetween>
    </Modal>
  );
};
